import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function getOptimizedIconUrl(
  originalUrl: string | null | undefined,
  size: number = 120,
  format: 'webp' | 'jpg' | 'png' = 'webp',
): string {
  if (!originalUrl || typeof originalUrl !== 'string' || originalUrl.trim() === '') return ''
  if (originalUrl.startsWith('data:')) return ''
  const instructionRegex = /\d+x\d+bb\.(jpg|png|webp)$/i
  const newInstruction = `${size}x${size}bb.${format}`
  return originalUrl.replace(instructionRegex, newInstruction)
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never'
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'Invalid date'
  }
}

/**
 * Pick the freshest "last seen" timestamp for a device.
 *
 * The app.devices.lastSeen column only updates on mdm.TokenUpdate / mdm.Connect
 * webhooks. Between webhooks (or if one was missed) NanoMDM's own
 * enrollments.last_seen_at is usually newer — the device-detail endpoint joins
 * that onto the response as nanomdm.lastSeenAt, and the status endpoint
 * surfaces it as nanomdmStatus.lastSeen. Take the max of whatever we have so
 * the displayed timestamp and the derived online/offline badge stay in sync
 * and don't flash a week-stale value on iOS.
 */
export function resolveLastSeen(
  ...sources: Array<string | null | undefined>
): string | null {
  let best: string | null = null
  let bestMs = -Infinity
  for (const source of sources) {
    if (!source) continue
    const ms = Date.parse(source)
    if (Number.isFinite(ms) && ms > bestMs) {
      bestMs = ms
      best = source
    }
  }
  return best
}

export function getDeviceStatus(lastSeen: string | null, isEnrolled: boolean = true): 'online' | 'offline' {
  if (!isEnrolled) return 'offline'
  if (!lastSeen) return 'offline'
  try {
    const lastSeenDate = new Date(lastSeen)
    const now = new Date()
    const diffMs = now.getTime() - lastSeenDate.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    return diffMins < 5 ? 'online' : 'offline'
  } catch {
    return 'offline'
  }
}

export function getiPhoneModelName(model: string | null): string {
  if (!model) return 'Unknown Device'
  const modelMap: Record<string, string> = {
    'iPhone16,1': 'iPhone 15 Pro',
    'iPhone16,2': 'iPhone 15 Pro Max',
    'iPhone16,3': 'iPhone 15 Plus',
    'iPhone16,4': 'iPhone 15',
    'iPhone15,2': 'iPhone 14 Pro Max',
    'iPhone15,3': 'iPhone 14 Pro',
    'iPhone15,4': 'iPhone 14 Plus',
    'iPhone15,5': 'iPhone 14',
    'iPhone14,2': 'iPhone 13 Pro Max',
    'iPhone14,3': 'iPhone 13 Pro',
    'iPhone14,4': 'iPhone 13 mini',
    'iPhone14,5': 'iPhone 13',
    'iPhone13,1': 'iPhone 12 mini',
    'iPhone13,2': 'iPhone 12',
    'iPhone13,3': 'iPhone 12 Pro',
    'iPhone13,4': 'iPhone 12 Pro Max',
    'iPhone12,1': 'iPhone 11',
    'iPhone12,3': 'iPhone 11 Pro',
    'iPhone12,5': 'iPhone 11 Pro Max',
    'iPhone12,8': 'iPhone SE (2nd gen)',
    'iPhone14,6': 'iPhone SE (3rd gen)',
  }
  if (modelMap[model]) return modelMap[model]
  const normalized = model.trim()
  if (modelMap[normalized]) return modelMap[normalized]
  if (normalized.toLowerCase().startsWith('iphone')) return normalized.replace(/,/g, ', ')
  return model
}
